// src/utils/themeUtils.ts
import themeColorsConfig from "@core/config/themeColors.json"

export interface CustomThemeColors {
  borderColor: string
  hoverBg: string
  borderColorTreatmentTag?: string
  bgColorTreatmentTag: string
  borderColorReceiptTag?: string
  bgColorReceiptTag?: string
  borderColorMemoTag?: string
  bgColorMemoTag?: string
  borderColorPatientCard?: string
  bgColorPatientCard?: string
  hoverBgPatientCard?: string
  selectedBgPatientCard?: string
  borderColorInsurance1Tag?: string
  bgColorInsurance1Tag?: string
  borderColorInsurance2Tag?: string
  bgColorInsurance2Tag?: string
  borderColorInsurance3Tag?: string
  bgColorInsurance3Tag?: string
  borderColorInsurance4Tag?: string
  bgColorInsurance4Tag?: string
  borderColorEncounterItemTag?: string
  bgColorEncounterItemTag?: string
  textColorTreatmentTag?: string
  globalBgColor?: string
  panelBgColor?: string
  informationBgColor?: string
  buttonBgColor?: string
  cardHoverColor?: string
  cardNormalColor?: string
  cardSelectedColor?: string
  panelHeaderColor?: string
  subInfoBgColor?: string
  encounterBgColor?: string
  encounterContentsBgColor?: string
  infoTableBgColor?: string
  infoTablePastelColor?: string
  layer1Transparency?: number
  layer2Transparency?: number
  layer3Transparency?: number
  layer4Transparency?: number
  mainFontColor?: string
  subFontColor?: string
  topBarHeight?: number
  sideToggleButtonColor?: string
  sideRoundedButtonColor?: string
  informationTitleFontColor?: string
  informationTitleColor?: string
  historyAccentColor?: string
  dashboardTitleFontColor?: string
  transitionHoverColor?: string
  trialBgColor?: string
  isDarkMode?: boolean
  emptyBoxHeight?: number
  // 배경 이미지 설정
  useBackgroundImage?: boolean
  backgroundImagePath?: string
  backgroundColor?: string
}

/**
 * 외부 설정 파일에서 커스텀 테마 색상을 로드합니다
 */
export const loadCustomThemeColors = (): CustomThemeColors => {
  try {
    // JSON 파일에서 설정 로드
    const config = themeColorsConfig

    return {
      ...config.colors,
      ...config.transparencies,
      ...config.dimensions,
      ...config.background,
    } as CustomThemeColors
  } catch (error) {
    console.warn("테마 색상 설정 파일을 로드할 수 없습니다. 기본값을 사용합니다.", error)

    // 기본값 반환
    return getDefaultThemeColors()
  }
}

/**
 * 기본 테마 색상을 반환합니다
 */
export const getDefaultThemeColors = (): CustomThemeColors => {
  return {
    borderColor: "#ccc",
    hoverBg: "rgba(189,192,26,0.1)",
    borderColorTreatmentTag: "rgb(116, 116, 116)",
    bgColorTreatmentTag: "rgb(116, 116, 116)",
    borderColorReceiptTag: "rgb(189, 192, 26)",
    bgColorReceiptTag: "rgb(189, 192, 26)",
    borderColorMemoTag: "rgb(189, 192, 26)",
    bgColorMemoTag: "rgb(189, 192, 26)",
    borderColorPatientCard: "#ccc",
    bgColorPatientCard: "#fff",
    hoverBgPatientCard: "rgba(189,192,26,0.1)",
    selectedBgPatientCard: "rgb(174, 210, 238)",
    borderColorInsurance1Tag: "rgb(255, 0, 0)",
    bgColorInsurance1Tag: "rgb(255, 0, 0)",
    borderColorInsurance2Tag: "rgb(0, 255, 0)",
    bgColorInsurance2Tag: "rgb(0, 255, 0)",
    borderColorInsurance3Tag: "rgb(0, 0, 255)",
    bgColorInsurance3Tag: "rgb(0, 0, 255)",
    borderColorInsurance4Tag: "rgb(255, 255, 0)",
    bgColorInsurance4Tag: "rgb(255, 255, 0)",
    borderColorEncounterItemTag: "rgb(189, 192, 26)",
    bgColorEncounterItemTag: "rgb(189, 192, 26)",
    textColorTreatmentTag: "#ccc",
    globalBgColor: "#f2f3efff",
    panelBgColor: "#fff",
    informationBgColor: "#eddfdf",
    buttonBgColor: "#fff",
    cardHoverColor: "rgba(221, 236, 247,0.8)",
    cardNormalColor: "rgb(255,255,255)",
    cardSelectedColor: "#77e3f7",
    panelHeaderColor: "rgba(255,255,255,0.4)",
    subInfoBgColor: "#F9FAFB",
    encounterBgColor: "#ebf4fc",
    encounterContentsBgColor: "#fff",
    infoTableBgColor: "#fff",
    infoTablePastelColor: "#f0f8ff",
    layer1Transparency: 0.2,
    layer2Transparency: 0.6,
    layer3Transparency: 0.75,
    layer4Transparency: 1.0,
    mainFontColor: "#111",
    subFontColor: "#555",
    topBarHeight: 80,
    sideToggleButtonColor: "rgba(255,255,255, 0.5)",
    sideRoundedButtonColor: "rgba(255,255,255,0.5)",
    informationTitleFontColor: "#26789e",
    informationTitleColor: "#5b90b7",
    historyAccentColor: "#a45c5c",
    dashboardTitleFontColor: "#444",
    transitionHoverColor: "#1976d2",
    trialBgColor: "rgba(255, 127, 80, 0.9)",
    isDarkMode: false,
    emptyBoxHeight: 440,
  }
}

/**
 * 다크 모드 테마 색상을 반환합니다
 */
export const getDarkThemeColors = (): CustomThemeColors => {
  return {
    borderColor: "#404040",
    hoverBg: "rgba(189,192,26,0.2)",
    borderColorTreatmentTag: "rgb(136, 136, 136)",
    bgColorTreatmentTag: "rgb(136, 136, 136)",
    globalBgColor: "#1a1a1a",
    // panelBgColor: "#2d2d2d",
    panelBgColor: "#ffffff",
    informationBgColor: "#3a3a3a",
    buttonBgColor: "#2d2d2d",
    cardHoverColor: "rgba(80, 120, 160, 0.8)",
    cardNormalColor: "#2d2d2d",
    cardSelectedColor: "#4a90e2",
    panelHeaderColor: "rgba(80, 80, 80, 0.4)",
    subInfoBgColor: "#232323",
    encounterBgColor: "#2a3f4f",
    encounterContentsBgColor: "#a1a1a1",
    infoTableBgColor: "#2d2d2d",
    infoTablePastelColor: "#3a5a7a",
    layer1Transparency: 0.2,
    layer2Transparency: 0.2,
    layer3Transparency: 0.2,
    layer4Transparency: 1.0,
    mainFontColor: "#ffffff",
    subFontColor: "#e0e0e0",
    topBarHeight: 80,
    // sideToggleButtonColor: "rgba(80,80,80, 0.5)",
    // sideRoundedButtonColor: "rgba(80,80,80,0.5)",
    sideToggleButtonColor: "rgba(255,255,255, 0.2)",
    sideRoundedButtonColor: "rgba(255,255,255, 0.2)",
    informationTitleFontColor: "#6bb6ff",
    informationTitleColor: "#7bb5f0",
    historyAccentColor: "#e08a8a", // #a45c5c의 밝은 버전
    dashboardTitleFontColor: "#c0c0c0",
    transitionHoverColor: "#4a90e2",
    isDarkMode: true,
    emptyBoxHeight: 440,
  }
}

/**
 * 런타임에 색상을 동적으로 업데이트합니다
 */
export const updateThemeColor = (colorKey: keyof CustomThemeColors, value: string | number) => {
  // 로컬 스토리지에 저장
  const customColors = JSON.parse(localStorage.getItem("customThemeColors") || "{}")
  customColors[colorKey] = value
  localStorage.setItem("customThemeColors", JSON.stringify(customColors))
}

/**
 * 로컬 스토리지에서 커스텀 색상을 로드합니다
 */
export const loadCustomColorsFromStorage = (): Partial<CustomThemeColors> => {
  try {
    const stored = localStorage.getItem("customThemeColors")
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.warn("로컬 스토리지에서 테마 색상을 로드할 수 없습니다.", error)
    return {}
  }
}

/**
 * 다크 모드를 토글합니다
 */
export const toggleDarkMode = () => {
  const currentColors = loadCustomColorsFromStorage()
  const isDarkMode = currentColors.isDarkMode || false

  let newColors: CustomThemeColors
  if (isDarkMode) {
    // 라이트 모드로 전환
    newColors = getDefaultThemeColors()
  } else {
    // 다크 모드로 전환
    newColors = getDarkThemeColors()
  }

  // 로컬 스토리지에 새로운 색상 저장
  localStorage.setItem("customThemeColors", JSON.stringify(newColors))

  return newColors
}
